import { useEffect, useMemo, useState } from 'react';
import { FfpBonusInfoDocument, FfpBonusInfoQuery, FfpBonusInfoQueryVariables, SegmentFragment } from '@websky/graphql';
import { createClient } from '../../../../../graphql-client';
import { FfpBonusInfoById } from '../model/types';
import { useConfig } from '../../../../../context';
import { useTranslation } from 'react-i18next';
import { hasCodeSharedSegments } from '@shared/lib/flight';

export const useGetFfpBonusInfoById = ({
	legId,
	flightIds,
	segments,
	needToMakeRequest
}: {
	legId?: number;
	flightIds: string[];
	segments?: SegmentFragment[];
	needToMakeRequest?: boolean;
}) => {
	const client = createClient();

	const { t } = useTranslation('FareGroup');

	const [ffpBonusInfoById, setFfpBonusInfoById] = useState<FfpBonusInfoById>({});

	const [loading, setLoading] = useState(false);

	const {
		SearchForm: { payByMiles, showFfpBonusInfoInFares }
	} = useConfig();

	/** Есть ли хоть один КШ сегмент */
	const isCodeShared = useMemo(() => hasCodeSharedSegments(segments), [segments]);

	/**
	 * Запрашиваем информацию о милях если:
	 * - нужно запрашивать мили (needToMakeRequest === true)
	 * - и у а/к есть программа лояльности
	 * - и у а/к включен показ начисляемых миль
	 * - и рейс не является КШ
	 */
	const mustBeRequested = needToMakeRequest && payByMiles && showFfpBonusInfoInFares && !isCodeShared;

	useEffect(() => {
		if (!mustBeRequested) {
			return;
		}

		/**
		 * Если для flightId ранее не запрашивались мили, то сохраняем его с loading = true, для отображения загрузки.
		 * И сохраняем прошлые состояния, чтобы по новой не показывать лоудер.
		 */
		setFfpBonusInfoById(prevState => {
			const nextState: FfpBonusInfoById = {};

			flightIds.forEach(flightId => {
				if (flightId && !(flightId in prevState)) {
					nextState[flightId] = {
						loading: true
					};
				}
			});

			return { ...prevState, ...nextState };
		});
	}, [flightIds, mustBeRequested]);

	useEffect(() => {
		if (!mustBeRequested) {
			return;
		}

		const getFfpBonusInfo = async () => {
			setLoading(true);

			const promises = flightIds.map(async flightId => {
				try {
					const { data } = await client.query<FfpBonusInfoQuery, FfpBonusInfoQueryVariables>({
						query: FfpBonusInfoDocument,
						variables: { flightId },
						fetchPolicy: 'cache-first'
					});

					const error = t('FfpBonusInfo_Error');

					const ffpBonusInfo = data?.FfpBonusInfo;
					const ffpBonusInfoByLeg = ffpBonusInfo?.bonusMilesByLeg?.find(info => info.legId === legId);

					if (!ffpBonusInfo) {
						return { flightId, ffpBonusInfo: { loading: false, error } };
					}

					let bonusMiles;

					if (ffpBonusInfoByLeg) {
						/** Если есть legId и для этого плеча есть разбивака, то возвращаем количество миль для конкретного плеча */
						bonusMiles = ffpBonusInfoByLeg?.bonusMiles;
					} else {
						/** Если нет, то выводим сумму миль за весь перелет */
						bonusMiles = ffpBonusInfo?.bonusMiles;
					}

					return {
						flightId,
						ffpBonusInfo: { bonusMiles, loading: false }
					};
				} catch (e) {
					return {
						flightId,
						ffpBonusInfo: {
							loading: false,
							error: t('FfpBonusInfo_Error')
						}
					};
				}
			});

			const responses = await Promise.all(promises);

			const result: FfpBonusInfoById = {};

			responses.forEach(({ flightId, ffpBonusInfo }) => {
				result[flightId] = ffpBonusInfo;
			});

			setFfpBonusInfoById(prev => ({
				...prev,
				...result
			}));

			setLoading(false);
		};

		getFfpBonusInfo();
	}, [flightIds, client, legId, mustBeRequested]);

	return {
		ffpBonusInfoById,
		loading
	};
};
